import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, Loader2, Sparkles, TrendingUp, AlertCircle, ChevronRight, LogIn, ShoppingBag, WifiOff } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getCategoryValue } from "@/lib/categories";
import { useIsMobile } from "@/hooks/use-mobile";
import { queueSnap } from "@/lib/offlineSnapQueue";

interface ValuationResult {
  item_name: string;
  category: string;
  condition_grade: string;
  condition_notes: string;
  price_estimate: {
    low: number;
    mid: number;
    high: number;
    currency: string;
  };
  confidence: "low" | "medium" | "high";
  market_insights: string;
  authentication_notes: string;
  comparable_sales: string[];
}

interface SnapToPriceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onValuationComplete?: (valuation: ValuationResult, imageUrl: string) => void;
}

const GUEST_SCAN_KEY = "snap_to_price_guest_count";
const GUEST_SCAN_LIMIT = 3;

function getGuestScanCount(): number {
  try {
    return parseInt(localStorage.getItem(GUEST_SCAN_KEY) || "0", 10);
  } catch {
    return 0;
  }
}

function incrementGuestScanCount(): void {
  try {
    const current = getGuestScanCount();
    localStorage.setItem(GUEST_SCAN_KEY, String(current + 1));
  } catch {
    // localStorage unavailable
  }
}

export function SnapToPriceModal({ open, onOpenChange, onValuationComplete }: SnapToPriceModalProps) {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageMimeType, setImageMimeType] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [valuation, setValuation] = useState<ValuationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGuest, setIsGuest] = useState(false);
  const [guestLimitReached, setGuestLimitReached] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const hasAutoTriggered = useRef(false);

  // On mobile, auto-open camera when modal opens
  useEffect(() => {
    if (open && isMobile && !imagePreview && !hasAutoTriggered.current) {
      hasAutoTriggered.current = true;
      // Small delay to let the modal render
      const timer = setTimeout(() => {
        cameraInputRef.current?.click();
      }, 300);
      return () => clearTimeout(timer);
    }
    if (!open) {
      hasAutoTriggered.current = false;
    }
  }, [open, isMobile, imagePreview]);

  const resetState = useCallback(() => {
    setImagePreview(null);
    setImageBase64(null);
    setImageMimeType(null);
    setValuation(null);
    setError(null);
    setIsAnalyzing(false);
    setIsGuest(false);
    setGuestLimitReached(false);
  }, []);

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  const processImage = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setImagePreview(result);
      setImageMimeType(file.type || "image/jpeg");
      const base64 = result.split(",")[1];
      setImageBase64(base64);
      setError(null);
      setGuestLimitReached(false);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError("Image must be under 10MB");
        return;
      }
      processImage(file);
    }
  };

  const analyzeImage = async () => {
    if (!imageBase64) return;

    // Offline check — queue for later
    if (!navigator.onLine) {
      try {
        await queueSnap(imageBase64, imageMimeType || "image/jpeg");
        toast.info("Saved for analysis when you're back online", {
          icon: <WifiOff className="h-4 w-4" />,
        });
        handleClose();
      } catch {
        toast.error("Failed to save image for later");
      }
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    const isLoggedIn = !!session?.access_token;

    if (!isLoggedIn && getGuestScanCount() >= GUEST_SCAN_LIMIT) {
      setGuestLimitReached(true);
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const headers: Record<string, string> = {};
      if (isLoggedIn) {
        headers.Authorization = `Bearer ${session!.access_token}`;
      }

      const { data, error: fnError } = await supabase.functions.invoke("snap-to-price", {
        headers,
        body: { imageBase64, imageMimeType },
      });

      if (fnError) throw fnError;

      if (data.error) {
        if (data.guestLimitReached) {
          setGuestLimitReached(true);
          return;
        }
        throw new Error(data.error);
      }

      setValuation(data.valuation);
      setIsGuest(data.isGuest || false);

      if (!isLoggedIn) {
        incrementGuestScanCount();
      }
      
      if (onValuationComplete && imagePreview) {
        onValuationComplete(data.valuation, imagePreview);
      }

      // Dispatch snap_completed event for install prompt timing
      window.dispatchEvent(new Event("snap_completed"));

      toast.success("Valuation complete!");
    } catch (err) {
      console.error("Valuation error:", err);
      const message = err instanceof Error ? err.message : "Failed to analyze image";
      setError(message);
      toast.error(message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const confidenceColors = {
    low: "text-amber-500 bg-amber-500/10",
    medium: "text-blue-500 bg-blue-500/10",
    high: "text-emerald-500 bg-emerald-500/10",
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const remainingGuest = GUEST_SCAN_LIMIT - getGuestScanCount();

  const handleListItem = () => {
    if (!valuation || !imagePreview) return;

    const conditionMap: Record<string, string> = {
      "Mint": "A", "Near Mint": "A", "Excellent": "A",
      "Very Good": "B", "Good": "B",
      "Fair": "C", "Poor": "C",
    };
    const mappedCondition = conditionMap[valuation.condition_grade] || "B";

    const listingData = {
      title: valuation.item_name,
      category: getCategoryValue(valuation.category) || valuation.category,
      condition: mappedCondition,
      estimatedPrice: valuation.price_estimate.mid,
      description: valuation.market_insights || "",
      brand: "",
      model: "",
      attributes: [],
      imageUrl: imagePreview,
    };
    sessionStorage.setItem("snap_to_price_listing", JSON.stringify(listingData));

    const checkAndNavigate = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      handleClose();
      if (session?.user) {
        navigate("/sell?fromSnap=true");
      } else {
        sessionStorage.setItem("redirectAfterAuth", "/sell?fromSnap=true");
        navigate("/auth");
      }
    };
    checkAndNavigate();
  };

  // Shared modal content
  const modalBody = (
    <div className="space-y-4">
      <AnimatePresence mode="wait">
        {!imagePreview ? (
          <motion.div
            key="upload"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <p className="text-sm text-muted-foreground">
              {isMobile
                ? "Point your camera at a collectible for an instant AI valuation."
                : "Take a photo or upload an image of your collectible for an instant AI-powered valuation."}
            </p>

            <div className={cn("grid gap-3", isMobile ? "grid-cols-1" : "grid-cols-2")}>
              <Button
                variant="outline"
                className={cn(
                  "flex-col gap-3 border-dashed hover:border-primary hover:bg-primary/5",
                  isMobile ? "h-24" : "h-32"
                )}
                onClick={() => cameraInputRef.current?.click()}
              >
                <Camera className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm">Take Photo</span>
              </Button>

              <Button
                variant="outline"
                className={cn(
                  "flex-col gap-3 border-dashed hover:border-primary hover:bg-primary/5",
                  isMobile ? "h-16" : "h-32"
                )}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className={cn("text-muted-foreground", isMobile ? "h-5 w-5" : "h-8 w-8")} />
                <span className="text-sm">Upload from gallery</span>
              </Button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileChange}
            />
          </motion.div>
        ) : guestLimitReached ? (
          <motion.div
            key="guest-limit"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4 text-center py-4"
          >
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">You've used your 3 free scans</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Sign in to get unlimited valuations, save your results, and track your collection.
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={resetState}>
                Go Back
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  sessionStorage.setItem("redirectAfterAuth", window.location.pathname);
                  window.location.href = "/auth";
                }}
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign In
              </Button>
            </div>
          </motion.div>
        ) : !valuation ? (
          <motion.div
            key="preview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            <div className="relative rounded-lg overflow-hidden bg-muted">
              <img
                src={imagePreview}
                alt="Preview"
                className={cn("w-full object-contain", isMobile ? "h-48" : "h-64")}
              />
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm"
                onClick={resetState}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            <Button
              className="w-full min-h-[48px]"
              size="lg"
              onClick={analyzeImage}
              disabled={isAnalyzing}
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Get Valuation
                </>
              )}
            </Button>
          </motion.div>
        ) : (
          <motion.div
            key="result"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {isGuest && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20">
                <p className="text-sm text-muted-foreground">
                  {remainingGuest > 0 
                    ? `${remainingGuest} free scan${remainingGuest !== 1 ? 's' : ''} remaining.`
                    : "Last free scan!"
                  } Sign in for unlimited scans.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-primary"
                  onClick={() => {
                    sessionStorage.setItem("redirectAfterAuth", window.location.pathname);
                    window.location.href = "/auth";
                  }}
                >
                  Sign In
                </Button>
              </div>
            )}

            <div className="flex gap-4">
              <img
                src={imagePreview}
                alt="Item"
                className="w-20 h-20 object-cover rounded-lg"
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold truncate">{valuation.item_name}</h3>
                <p className="text-sm text-muted-foreground">{valuation.category}</p>
                <div className={cn(
                  "inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium",
                  confidenceColors[valuation.confidence]
                )}>
                  {valuation.confidence} confidence
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Estimated Value</span>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">
                  {formatPrice(valuation.price_estimate.mid)}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  Range: {formatPrice(valuation.price_estimate.low)} - {formatPrice(valuation.price_estimate.high)}
                </div>
              </div>
            </div>

            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Condition</span>
                <span className="font-medium">{valuation.condition_grade}</span>
              </div>
              {valuation.condition_notes && (
                <p className="text-xs text-muted-foreground mt-1">{valuation.condition_notes}</p>
              )}
            </div>

            {valuation.market_insights && (
              <div className="p-3 rounded-lg bg-muted/50">
                <span className="text-sm font-medium">Market Insights</span>
                <p className="text-sm text-muted-foreground mt-1">{valuation.market_insights}</p>
              </div>
            )}

            {valuation.authentication_notes && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <span className="text-sm font-medium text-amber-600 dark:text-amber-400">Authentication Notes</span>
                <p className="text-sm text-muted-foreground mt-1">{valuation.authentication_notes}</p>
              </div>
            )}

            {valuation.comparable_sales && valuation.comparable_sales.length > 0 && (
              <div className="space-y-2">
                <span className="text-sm font-medium">Recent Comparables</span>
                {valuation.comparable_sales.map((sale, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ChevronRight className="h-3 w-3" />
                    {sale}
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 min-h-[48px]" onClick={resetState}>
                Scan Another
              </Button>
              <Button className="flex-1 min-h-[48px]" onClick={handleListItem}>
                <ShoppingBag className="h-4 w-4 mr-2" />
                List This Item
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Use Drawer on mobile, Dialog on desktop
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleClose}>
        <DrawerContent className="max-h-[92vh] overflow-y-auto px-4 pb-8">
          <DrawerHeader className="text-left px-0">
            <DrawerTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Snap to Price
            </DrawerTitle>
          </DrawerHeader>
          {modalBody}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Snap to Price
          </DialogTitle>
        </DialogHeader>
        {modalBody}
      </DialogContent>
    </Dialog>
  );
}
